import { useEffect, useLayoutEffect, useState } from 'react';

import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals } from '@confluence/feature-experiments';

import { getIsSticky } from './isStickyUtilities.ts';

type UseIsStickyParams = {
	isEditor?: boolean;
	elementToObserve?: HTMLElement | null;
};

export const useIsSticky = ({ isEditor, elementToObserve }: UseIsStickyParams) => {
	const isStickyHeaderExperimentEnabled =
		expValEquals(
			'platform_editor_table_sticky_header_improvements',
			'cohort',
			'test_with_overflow',
		) && expVal('platform_editor_table_sticky_header_patch_11', 'isEnabled', false);
	// check if table is already sticky
	const initialStickyState = elementToObserve
		? getIsSticky(
				isEditor && isStickyHeaderExperimentEnabled
					? elementToObserve
					: elementToObserve.parentElement,
				isEditor,
			)
		: false;

	const [isSticky, setIsSticky] = useState(
		fg('confluence_frontend_remove_duplicate_sidebar_logic') && initialStickyState,
	);

	// Sticky state effect for post-render (useEffect)
	useEffect(() => {
		if (!fg('confluence_frontend_remove_duplicate_sidebar_logic')) {
			return;
		}

		if (expValEquals('confluence_compute_sidebar_styles_pre-render', 'isEnabled', true)) {
			return;
		}

		setIsSticky(initialStickyState);
	}, [initialStickyState]);

	// Sticky state effect for pre-render (useLayoutEffect)
	useLayoutEffect(() => {
		if (!fg('confluence_frontend_remove_duplicate_sidebar_logic')) {
			return;
		}

		if (!expValEquals('confluence_compute_sidebar_styles_pre-render', 'isEnabled', true)) {
			return;
		}

		setIsSticky(initialStickyState);
	}, [initialStickyState]);

	return [isSticky, setIsSticky] as const;
};
